<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import QRCode from 'qrcode';
import { useAlert } from 'dashboard/composables';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from 'dashboard/components/Modal.vue';
import InboxName from 'dashboard/components/widgets/InboxName.vue';
import InboxesAPI from 'dashboard/api/inboxes';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const rawConnectionStatus = ref('');
const qrCode = ref(null);
const pairCode = ref('');
const MODAL_MODES = {
  QR: 'qr',
  PAIR: 'pair',
};
const modalMode = ref(MODAL_MODES.QR);
const requestedModalMode = ref(MODAL_MODES.QR);
const showQrModal = ref(false);
const isGeneratingQr = ref(false);
const isGeneratingPair = ref(false);
const isDisconnecting = ref(false);
const isRecreating = ref(false);
const inboxPhoneNumber = computed(() => {
  const phone = props.inbox?.phone_number || props.inbox?.phoneNumber || '';
  return phone ? phone.toString().trim() : '';
});

const normalizeStatus = status => {
  if (status === undefined || status === null) {
    return '';
  }

  return status.toString().trim().toLowerCase();
};

const isConnectedStatus = status => normalizeStatus(status) === 'connected';

const closeQrModalForStatus = status => {
  if (isConnectedStatus(status)) {
    qrCode.value = null;
    showQrModal.value = false;
    modalMode.value = MODAL_MODES.QR;
    requestedModalMode.value = MODAL_MODES.QR;
  }
};

const STATUS_TRANSLATIONS = {
  connected: 'INBOX_MGMT.ADD.UAZAPI.STATUS.CONNECTED',
  connecting: 'INBOX_MGMT.ADD.UAZAPI.STATUS.CONNECTING',
  disconnected: 'INBOX_MGMT.ADD.UAZAPI.STATUS.DISCONNECTED',
  disconnectedqr: 'INBOX_MGMT.ADD.UAZAPI.STATUS.DISCONNECTED',
  disconnected_qr: 'INBOX_MGMT.ADD.UAZAPI.STATUS.DISCONNECTED',
  scan_qr_code: 'INBOX_MGMT.ADD.UAZAPI.STATUS.SCAN_QR_CODE',
  'scan-qr-code': 'INBOX_MGMT.ADD.UAZAPI.STATUS.SCAN_QR_CODE',
  'scan qr code': 'INBOX_MGMT.ADD.UAZAPI.STATUS.SCAN_QR_CODE',
  waiting_qr: 'INBOX_MGMT.ADD.UAZAPI.STATUS.WAITING_QR',
  waitingqr: 'INBOX_MGMT.ADD.UAZAPI.STATUS.WAITING_QR',
  qrcode: 'INBOX_MGMT.ADD.UAZAPI.STATUS.WAITING_QR',
};

const formatStatus = status => {
  const normalized = normalizeStatus(status);

  if (!normalized) {
    return t('INBOX_MGMT.ADD.UAZAPI.STATUS.UNKNOWN');
  }

  const translationKey = STATUS_TRANSLATIONS[normalized];

  if (translationKey) {
    return t(translationKey);
  }

  return normalized
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, letter => letter.toUpperCase());
};

const connectionStatusLabel = computed(() =>
  formatStatus(rawConnectionStatus.value)
);

const isConnected = computed(() =>
  isConnectedStatus(rawConnectionStatus.value)
);

const connectionStatusClass = computed(() => {
  const status = normalizeStatus(rawConnectionStatus.value);

  if (['connected'].includes(status)) {
    return 'text-green-600';
  }

  if (
    [
      'connecting',
      'scan_qr_code',
      'scan-qr-code',
      'scan qr code',
      'waiting_qr',
      'waitingqr',
      'qrcode',
    ].includes(status)
  ) {
    return 'text-yellow-600';
  }

  if (!status) {
    return 'text-gray-600';
  }

  return 'text-red-600';
});

const toDataUrl = async value => {
  if (!value) return null;

  const trimmed = value.toString().trim();
  if (!trimmed) return null;

  if (trimmed.startsWith('data:image')) {
    return trimmed;
  }

  const compact = trimmed.replace(/\s+/g, '');

  const isBase64 = () => {
    try {
      return btoa(atob(compact)) === compact;
    } catch (error) {
      return false;
    }
  };

  if (isBase64()) {
    return `data:image/png;base64,${compact}`;
  }

  return QRCode.toDataURL(trimmed);
};

const normalizeQrPayload = async payload => {
  if (!payload) {
    return null;
  }

  if (typeof payload === 'string') {
    return toDataUrl(payload);
  }

  if (payload && typeof payload === 'object') {
    const candidate =
      payload.base64 ||
      payload.qrcode?.base64 ||
      payload.qrcode?.code ||
      payload.qrcode ||
      payload.code ||
      payload.value ||
      null;

    return toDataUrl(candidate);
  }

  return null;
};

const candidateHasValue = value =>
  value !== undefined && value !== null && value !== '';

const extractStatusCandidates = payload => {
  if (!payload || typeof payload !== 'object') {
    return [];
  }

  return [
    payload.connection,
    payload.status,
    payload.state,
    payload?.response?.connection,
    payload?.response?.status,
    payload?.response?.state,
    payload?.status?.connection,
    payload?.status?.status,
    payload?.status?.state,
    payload?.instance?.status,
    payload?.instance?.state,
  ];
};

const extractPairCandidates = payload => {
  if (!payload || typeof payload !== 'object') {
    return [];
  }

  return [
    payload.paircode,
    payload.pairCode,
    payload.response?.paircode,
    payload.response?.pairCode,
    payload.instance?.paircode,
    payload.instance?.pairCode,
  ];
};

const extractQrCandidates = payload => {
  if (!payload || typeof payload !== 'object') {
    return [];
  }

  return [
    payload.qr_data_url,
    payload.qrDataUrl,
    payload.qrcode,
    payload.qrCode,
    payload.response?.qr_data_url,
    payload.response?.qrcode,
    payload.response?.qrCode,
    payload.instance?.qr_data_url,
    payload.instance?.qrcode,
    payload.instance?.qrCode,
  ];
};

const hasPairCode = payload =>
  extractPairCandidates(payload).some(candidateHasValue);

const hasQrCode = payload =>
  extractQrCandidates(payload).some(candidateHasValue);

const hasQrOrPair = payload => hasPairCode(payload) || hasQrCode(payload);

const syncModalModeForState = ({ hasQr, hasPair }) => {
  if (requestedModalMode.value === MODAL_MODES.PAIR && hasPair) {
    modalMode.value = MODAL_MODES.PAIR;
    return;
  }

  if (hasQr) {
    modalMode.value = MODAL_MODES.QR;
    return;
  }

  if (hasPair) {
    modalMode.value = MODAL_MODES.PAIR;
    return;
  }

  modalMode.value = MODAL_MODES.QR;
};

const isPairModal = computed(() => modalMode.value === MODAL_MODES.PAIR);
const isQrModal = computed(() => modalMode.value === MODAL_MODES.QR);

watch(
  () => props.inbox?.provider_connection,
  providerConnection => {
    const connectionCandidate =
      extractStatusCandidates(providerConnection).find(candidateHasValue) || '';

    rawConnectionStatus.value = connectionCandidate;
    closeQrModalForStatus(connectionCandidate);

    const providerPair =
      extractPairCandidates(providerConnection).find(candidateHasValue) || '';
    pairCode.value = providerPair
      ? providerPair.toString().trim().toUpperCase()
      : '';

    const providerQr =
      extractQrCandidates(providerConnection).find(candidateHasValue) || '';

    if (providerQr) {
      qrCode.value = providerQr;
    } else if (isConnectedStatus(connectionCandidate)) {
      qrCode.value = null;
    }

    syncModalModeForState({
      hasQr: !!providerQr,
      hasPair: !!providerPair,
    });
  },
  { immediate: true, deep: true }
);

const extractErrorMessage = error => {
  if (!error) return '';

  const errorData = error.response?.data;
  const candidate = errorData?.error ?? errorData;

  if (typeof candidate === 'string') {
    return candidate;
  }

  if (candidate && typeof candidate === 'object') {
    const nestedMessage =
      candidate.message || candidate.error || candidate.response;

    if (typeof nestedMessage === 'string') {
      return nestedMessage;
    }
  }

  return error.message;
};

const handleConnectionMetadata = data => {
  if (!data) return;

  const statusCandidate =
    extractStatusCandidates(data).find(candidateHasValue) || '';

  rawConnectionStatus.value = statusCandidate;
  closeQrModalForStatus(statusCandidate);

  const candidatePair =
    extractPairCandidates(data).find(candidateHasValue) || '';

  const normalizedPair = candidatePair ? candidatePair.toString().trim() : '';
  pairCode.value = normalizedPair ? normalizedPair.toUpperCase() : '';
};

const fetchInstanceStatus = async ({ silent = true } = {}) => {
  try {
    const response = await InboxesAPI.getUazapiInstanceStatus(props.inbox.id);
    return response.data || {};
  } catch (error) {
    if (!silent) {
      useAlert(
        extractErrorMessage(error) || t('INBOX_MGMT.ADD.UAZAPI.STATUS.ERROR')
      );
    }
    return null;
  }
};

const applyConnectionPayload = async (
  payload,
  { openModal = false, notify = false } = {}
) => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const nestedPayload =
    payload.provider_connection ||
    payload.connection_data ||
    payload.providerConnection ||
    payload.connectionData;

  const normalizedPayload =
    nestedPayload && typeof nestedPayload === 'object'
      ? { ...payload, ...nestedPayload }
      : payload;

  handleConnectionMetadata(normalizedPayload);

  const providerPair =
    extractPairCandidates(normalizedPayload).find(candidateHasValue) || '';
  const providerQr =
    extractQrCandidates(normalizedPayload).find(candidateHasValue) || '';

  const hasPair = !!(providerPair || pairCode.value);
  const hasQr = !!providerQr;

  syncModalModeForState({ hasQr, hasPair });

  if (isConnectedStatus(rawConnectionStatus.value)) {
    qrCode.value = null;
    showQrModal.value = false;
    modalMode.value = MODAL_MODES.QR;
    requestedModalMode.value = MODAL_MODES.QR;
    return null;
  }

  if (!openModal) {
    return null;
  }

  const qr = await normalizeQrPayload(providerQr || normalizedPayload);

  if (qr) {
    qrCode.value = qr;
    syncModalModeForState({ hasQr: true, hasPair });
    showQrModal.value = true;
    if (notify) {
      useAlert(t('INBOX_MGMT.ADD.UAZAPI.QR_CODE.SUCCESS'));
    }
  } else {
    qrCode.value = null;
    syncModalModeForState({ hasQr: false, hasPair });
    showQrModal.value = true;
    const messageKey = pairCode.value
      ? 'INBOX_MGMT.ADD.UAZAPI.QR_CODE.NO_QR_WITH_PAIR'
      : 'INBOX_MGMT.ADD.UAZAPI.QR_CODE.NO_QR';
    if (notify) {
      useAlert(t(messageKey));
    }
  }

  return qr;
};

const processConnectionResponse = async responsePayload => {
  let payload = responsePayload || {};

  const statusPayload = await fetchInstanceStatus({ silent: true });
  if (statusPayload) {
    await applyConnectionPayload(statusPayload);
    const statusHasQr = hasQrCode(statusPayload);
    const payloadHasQr = hasQrCode(payload);
    const statusHasQrOrPair = hasQrOrPair(statusPayload);
    const payloadHasQrOrPair = hasQrOrPair(payload);

    if (
      (statusHasQr && !payloadHasQr) ||
      (!payloadHasQrOrPair && statusHasQrOrPair)
    ) {
      payload = statusPayload;
    }
  }

  await applyConnectionPayload(payload, { openModal: true, notify: true });
};

const generateQrCode = async () => {
  try {
    isGeneratingQr.value = true;
    modalMode.value = MODAL_MODES.QR;
    requestedModalMode.value = MODAL_MODES.QR;
    qrCode.value = null;
    const response = await InboxesAPI.connectUazapiInstance(props.inbox.id, {
      phone: '',
    });
    await processConnectionResponse(response.data || {});
  } catch (error) {
    useAlert(
      extractErrorMessage(error) || t('INBOX_MGMT.ADD.UAZAPI.QR_CODE.ERROR')
    );
  } finally {
    isGeneratingQr.value = false;
  }
};

const generatePairCode = async () => {
  if (!inboxPhoneNumber.value) {
    useAlert(t('INBOX_MGMT.ADD.UAZAPI.PAIRING_CODE.MISSING_PHONE'));
    return;
  }

  try {
    isGeneratingPair.value = true;
    modalMode.value = MODAL_MODES.PAIR;
    requestedModalMode.value = MODAL_MODES.PAIR;
    qrCode.value = null;
    const response = await InboxesAPI.connectUazapiInstance(props.inbox.id, {
      phone: inboxPhoneNumber.value,
    });
    await processConnectionResponse(response.data || {});
  } catch (error) {
    useAlert(
      extractErrorMessage(error) ||
        t('INBOX_MGMT.ADD.UAZAPI.PAIRING_CODE.ERROR')
    );
  } finally {
    isGeneratingPair.value = false;
  }
};

const disconnectInstance = async () => {
  if (!props.inbox?.id || isDisconnecting.value) {
    return;
  }

  try {
    isDisconnecting.value = true;
    const response = await InboxesAPI.disconnectUazapiInstance(props.inbox.id);
    const payload = response.data || {};

    await applyConnectionPayload(payload);
    const statusPayload = await fetchInstanceStatus({ silent: true });
    await applyConnectionPayload(statusPayload);

    qrCode.value = null;
    modalMode.value = MODAL_MODES.QR;
    requestedModalMode.value = MODAL_MODES.QR;
    showQrModal.value = false;

    useAlert(t('INBOX_MGMT.ADD.UAZAPI.DISCONNECT.SUCCESS'));
  } catch (error) {
    useAlert(
      extractErrorMessage(error) || t('INBOX_MGMT.ADD.UAZAPI.DISCONNECT.ERROR')
    );
  } finally {
    isDisconnecting.value = false;
  }
};

const recreateInstance = async () => {
  if (!props.inbox?.id || isRecreating.value) return;

  try {
    isRecreating.value = true;

    await InboxesAPI.recreateUazapiInstance(props.inbox.id);

    useAlert('Session recreated successfully');
  } catch (error) {
    useAlert(
      'Failed to recreate session: ' + (error.message || 'Unknown error')
    );
  } finally {
    isRecreating.value = false;
  }
};

const closeQrModal = () => {
  modalMode.value = MODAL_MODES.QR;
  requestedModalMode.value = MODAL_MODES.QR;
  showQrModal.value = false;
};

const matchesInbox = payload => {
  if (!payload) {
    return false;
  }

  const incoming = payload.inbox_id ?? payload.inboxId;
  if (incoming !== undefined && incoming !== null) {
    return String(incoming) === String(props.inbox?.id);
  }

  const metaId = payload.metadata?.['chatwoot.inbox_id'];
  if (metaId !== undefined && metaId !== null) {
    return String(metaId) === String(props.inbox?.id);
  }

  return false;
};

const onConnectionUpdate = async payload => {
  if (!matchesInbox(payload)) {
    return;
  }

  await applyConnectionPayload(payload, { openModal: false });

  if (isConnectedStatus(rawConnectionStatus.value)) {
    return;
  }

  const statusPayload = await fetchInstanceStatus({ silent: true });
  await applyConnectionPayload(statusPayload);
};

onMounted(async () => {
  emitter.on(BUS_EVENTS.WHATSAPP_CONNECTION_UPDATE, onConnectionUpdate);

  if (!props.inbox?.id) {
    return;
  }

  const statusPayload = await fetchInstanceStatus({ silent: true });
  await applyConnectionPayload(statusPayload);
});

onUnmounted(() => {
  emitter.off(BUS_EVENTS.WHATSAPP_CONNECTION_UPDATE, onConnectionUpdate);
});

watch(
  () => props.inbox?.id,
  async newId => {
    if (!newId) {
      return;
    }

    const statusPayload = await fetchInstanceStatus({ silent: true });
    await applyConnectionPayload(statusPayload);
  }
);
</script>

<template>
  <div class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.ADD.UAZAPI.MANAGEMENT.TITLE')"
      :sub-title="$t('INBOX_MGMT.ADD.UAZAPI.MANAGEMENT.SUBTITLE')"
    >
      <div class="flex flex-col gap-4">
        <InboxName
          :inbox="inbox"
          class="!text-lg !m-0"
          with-phone-number
          with-provider-connection-status
        />

        <div class="flex items-center gap-2 text-sm">
          <span class="font-medium text-slate-11">
            {{ $t('INBOX_MGMT.ADD.UAZAPI.STATUS.LABEL') }}
          </span>
          <span class="font-semibold" :class="connectionStatusClass">
            {{ connectionStatusLabel }}
          </span>
        </div>

        <div class="flex flex-wrap gap-2">
          <NextButton
            v-if="!isConnected"
            :label="$t('INBOX_MGMT.ADD.UAZAPI.QR_CODE.BUTTON')"
            :is-loading="isGeneratingQr"
            @click="generateQrCode"
          />
          <NextButton
            v-if="!isConnected"
            :label="$t('INBOX_MGMT.ADD.UAZAPI.PAIRING_CODE.BUTTON')"
            :is-loading="isGeneratingPair"
            @click="generatePairCode"
          />

          <NextButton
            :label="$t('INBOX_MGMT.ADD.UAZAPI.RECREATE.BUTTON')"
            :is-loading="isRecreating"
            @click="recreateInstance"
          />

          <NextButton
            v-if="isConnected"
            color="ruby"
            :label="$t('INBOX_MGMT.ADD.UAZAPI.DISCONNECT.BUTTON')"
            :is-loading="isDisconnecting"
            @click="disconnectInstance"
          />
        </div>
      </div>
    </SettingsSection>

    <Modal v-model:show="showQrModal" :on-close="closeQrModal">
      <div
        v-if="isQrModal"
        class="flex w-full max-w-md flex-col items-center justify-center gap-5 p-6 mx-auto"
      >
        <h3 class="text-lg font-semibold text-center">
          {{ $t('INBOX_MGMT.ADD.UAZAPI.QR_CODE.TITLE') }}
        </h3>
        <p class="text-sm text-slate-9 text-center">
          {{
            qrCode
              ? $t('INBOX_MGMT.ADD.UAZAPI.QR_CODE.INSTRUCTIONS')
              : $t('INBOX_MGMT.ADD.UAZAPI.QR_CODE.MISSING_MESSAGE')
          }}
        </p>
        <img
          v-if="qrCode"
          :src="qrCode"
          alt="WhatsApp QR"
          class="h-64 w-64 mx-auto block"
        />
      </div>

      <div
        v-if="isPairModal"
        class="flex w-full max-w-md flex-col items-center justify-center gap-5 p-6 mx-auto"
      >
        <h3 class="text-lg font-semibold text-center">
          {{ $t('INBOX_MGMT.ADD.UAZAPI.PAIRING_CODE.LABEL') }}
        </h3>
        <p class="text-sm text-slate-9 text-center">
          {{ $t('INBOX_MGMT.ADD.UAZAPI.PAIRING_CODE.DESCRIPTION') }}
        </p>
        <div
          v-if="pairCode"
          class="flex w-full max-w-xs flex-col items-center gap-3 rounded-lg bg-n-alpha-2 px-6 py-6 text-center mx-auto"
        >
          <p class="text-xs font-semibold uppercase text-slate-9">
            {{ $t('INBOX_MGMT.ADD.UAZAPI.PAIRING_CODE.LABEL') }}
          </p>
          <p class="text-4xl font-bold tracking-widest text-slate-12">
            {{ pairCode }}
          </p>
        </div>
      </div>
    </Modal>
  </div>
</template>
